import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../home/components/Footer';

export default function HelpPage() {
  const [activeCategory, setActiveCategory] = useState('getting-started');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  const categories = [
    { id: 'getting-started', name: '快速入门', icon: 'ri-rocket-line' },
    { id: 'api-docs', name: 'API文档', icon: 'ri-code-line' },
    { id: 'integration', name: '集成指南', icon: 'ri-plug-line' },
    { id: 'troubleshooting', name: '故障排除', icon: 'ri-tools-line' },
    { id: 'billing', name: '计费问题', icon: 'ri-money-dollar-circle-line' },
    { id: 'security', name: '安全相关', icon: 'ri-shield-check-line' }
  ];

  const helpArticles = {
    'getting-started': [
      {
        title: '如何开始使用Adobe PDF Library SDK',
        description: '详细的入门指南，帮助您快速集成PDF处理功能',
        readTime: '5分钟',
        difficulty: '初级',
        tags: ['SDK', 'PDF', '入门']
      },
      {
        title: 'PDF表单扩展SDK快速配置',
        description: '学习如何在您的应用中快速配置和使用PDF表单功能',
        readTime: '8分钟',
        difficulty: '初级',
        tags: ['表单', 'SDK', '配置']
      },
      {
        title: '开发环境搭建指南',
        description: '完整的开发环境配置步骤和最佳实践',
        readTime: '12分钟',
        difficulty: '初级',
        tags: ['环境', '配置', '开发']
      }
    ],
    'api-docs': [
      {
        title: 'REST API 完整参考',
        description: '所有API端点的详细文档和示例代码',
        readTime: '20分钟',
        difficulty: '中级',
        tags: ['API', 'REST', '文档']
      },
      {
        title: 'SDK方法参考手册',
        description: '所有SDK方法的详细说明和使用示例',
        readTime: '15分钟',
        difficulty: '中级',
        tags: ['SDK', '方法', '参考']
      },
      {
        title: '错误代码和处理',
        description: '常见错误代码含义及相应的处理方法',
        readTime: '10分钟',
        difficulty: '中级',
        tags: ['错误', '处理', '调试']
      }
    ],
    'integration': [
      {
        title: 'Java应用集成指南',
        description: '在Java应用中集成PDF处理功能的完整指南',
        readTime: '25分钟',
        difficulty: '中级',
        tags: ['Java', '集成', 'PDF']
      },
      {
        title: '.NET平台集成教程',
        description: '在.NET应用中使用我们的SDK的详细教程',
        readTime: '22分钟',
        difficulty: '中级',
        tags: ['.NET', '集成', 'SDK']
      },
      {
        title: 'Python集成最佳实践',
        description: 'Python环境下的集成方法和性能优化技巧',
        readTime: '18分钟',
        difficulty: '中级',
        tags: ['Python', '集成', '优化']
      }
    ],
    'troubleshooting': [
      {
        title: '常见集成问题解决',
        description: '解决集成过程中遇到的常见问题和错误',
        readTime: '15分钟',
        difficulty: '中级',
        tags: ['问题', '解决', '集成']
      },
      {
        title: '性能优化指南',
        description: '提升PDF处理性能的技巧和最佳实践',
        readTime: '20分钟',
        difficulty: '高级',
        tags: ['性能', '优化', 'PDF']
      },
      {
        title: '内存使用优化',
        description: '减少内存占用和避免内存泄漏的方法',
        readTime: '12分钟',
        difficulty: '高级',
        tags: ['内存', '优化', '性能']
      }
    ],
    'billing': [
      {
        title: '计费方式说明',
        description: '了解我们的计费模式和价格结构',
        readTime: '8分钟',
        difficulty: '初级',
        tags: ['计费', '价格', '说明']
      },
      {
        title: '如何升级订阅计划',
        description: '升级到更高级别订阅计划的步骤指南',
        readTime: '5分钟',
        difficulty: '初级',
        tags: ['升级', '订阅', '计划']
      },
      {
        title: '发票和付款问题',
        description: '关于发票获取和付款相关的常见问题',
        readTime: '6分钟',
        difficulty: '初级',
        tags: ['发票', '付款', '问题']
      }
    ],
    'security': [
      {
        title: '数据安全最佳实践',
        description: '保护您的数据和应用安全的重要指南',
        readTime: '15分钟',
        difficulty: '中级',
        tags: ['安全', '数据', '实践']
      },
      {
        title: 'API密钥管理',
        description: '如何安全地管理和使用API密钥',
        readTime: '10分钟',
        difficulty: '中级',
        tags: ['API', '密钥', '管理']
      },
      {
        title: '合规性要求',
        description: '了解相关的合规性要求和认证标准',
        readTime: '12分钟',
        difficulty: '中级',
        tags: ['合规', '认证', '标准']
      }
    ]
  };

  const faqs = [
    {
      question: '如何获取API密钥？',
      answer: '您可以在控制台的"API管理"页面中生成新的API密钥。每个密钥都有特定的权限范围，请根据您的需求选择合适的权限级别。'
    },
    {
      question: 'SDK支持哪些编程语言？',
      answer: '我们的SDK支持Java、.NET、Python、Node.js、PHP等主流编程语言。每种语言都提供了完整的文档和示例代码。'
    },
    {
      question: '如何处理大文件的PDF转换？',
      answer: '对于大文件处理，建议使用异步API接口，并实现适当的重试机制。同时可以考虑将大文件分块处理以提高效率。'
    },
    {
      question: '是否提供技术支持？',
      answer: '是的，我们为所有付费用户提供技术支持。您可以通过邮件、在线聊天或电话联系我们的技术支持团队。'
    },
    {
      question: '如何报告安全漏洞？',
      answer: '如果您发现了安全漏洞，请立即发送邮件至security@documogul.com。我们有专门的安全团队会及时处理并给予相应奖励。'
    },
    {
      question: '可以申请试用吗？',
      answer: '当然可以！我们为所有新用户提供30天的免费试用期，包含完整功能和技术支持。您可以在官网注册试用账户。'
    }
  ];

  // 搜索功能：搜索所有分类的文章
  const getSearchResults = () => {
    if (!searchQuery.trim()) {
      return [];
    }

    const query = searchQuery.toLowerCase();
    const results: Array<{
      article: typeof helpArticles.billing[0];
      category: string;
      categoryName: string;
    }> = [];

    // 搜索所有分类的文章
    Object.entries(helpArticles).forEach(([categoryId, articles]) => {
      const categoryName = categories.find(cat => cat.id === categoryId)?.name || categoryId;
      
      articles.forEach(article => {
        const matchTitle = article.title.toLowerCase().includes(query);
        const matchDescription = article.description.toLowerCase().includes(query);
        const matchTags = article.tags.some(tag => tag.toLowerCase().includes(query));
        
        if (matchTitle || matchDescription || matchTags) {
          results.push({
            article,
            category: categoryId,
            categoryName
          });
        }
      });
    });

    return results;
  };

  // 搜索FAQ
  const getFilteredFAQs = () => {
    if (!searchQuery.trim()) {
      return faqs;
    }

    const query = searchQuery.toLowerCase();
    return faqs.filter(faq => 
      faq.question.toLowerCase().includes(query) ||
      faq.answer.toLowerCase().includes(query)
    );
  };

  const searchResults = getSearchResults();
  const filteredFAQs = getFilteredFAQs();
  const isSearching = searchQuery.trim().length > 0;

  // 当前分类的文章（非搜索模式）
  const filteredArticles = !isSearching 
    ? helpArticles[activeCategory as keyof typeof helpArticles] || []
    : [];

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case '初级': return 'bg-green-100 text-green-800';
      case '中级': return 'bg-yellow-100 text-yellow-800';
      case '高级': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-600 via-cyan-600 to-teal-700 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6 backdrop-blur-sm mt-[15px]">
              <i className="ri-question-answer-line text-3xl text-white"></i>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">帮助中心</h1>
            <p className="text-xl text-teal-100 mb-8 max-w-3xl mx-auto">
              找到您需要的答案，获取技术支持，学习最佳实践
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto relative">
              <div className="relative">
                <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg"></i>
                <input
                  type="text"
                  placeholder="搜索帮助文档、API参考、教程..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 rounded-xl border-0 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-teal-300 focus:outline-none text-lg"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Animated Background Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-white/5 rounded-full animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-24 h-24 bg-white/5 rounded-full animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white/5 rounded-full animate-pulse delay-500"></div>
      </section>

      {/* Quick Stats */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-text-line text-2xl text-teal-600"></i>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">200+</div>
              <div className="text-gray-600">帮助文档</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-code-line text-2xl text-cyan-600"></i>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">50+</div>
              <div className="text-gray-600">API示例</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-video-line text-2xl text-emerald-600"></i>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">30+</div>
              <div className="text-gray-600">视频教程</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-line text-2xl text-teal-600"></i>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">24/7</div>
              <div className="text-gray-600">技术支持</div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {isSearching ? (
            // 搜索结果视图
            <div>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  搜索结果
                </h2>
                <p className="text-gray-600">
                  找到 <strong className="text-blue-600">{searchResults.length}</strong> 篇文章和 <strong className="text-blue-600">{filteredFAQs.length}</strong> 个常见问题
                </p>
              </div>

              {searchResults.length > 0 && (
                <div className="mb-12">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">相关文章</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {searchResults.map((result, index) => (
                      <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded text-xs font-medium">
                                {result.categoryName}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(result.article.difficulty)}`}>
                                {result.article.difficulty}
                              </span>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">{result.article.title}</h3>
                          </div>
                        </div>
                        <p className="text-gray-600 mb-4">{result.article.description}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-sm text-gray-500">
                            <i className="ri-time-line mr-1"></i>
                            {result.article.readTime}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {result.article.tags.map((tag, tagIndex) => (
                              <span key={tagIndex} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {filteredFAQs.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">相关问题</h3>
                  <div className="space-y-4">
                    {filteredFAQs.map((faq, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg">
                        <button
                          onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <span className="font-medium text-gray-900">{faq.question}</span>
                          <i className={`ri-arrow-${expandedFAQ === index ? 'up' : 'down'}-s-line text-gray-500`}></i>
                        </button>
                        {expandedFAQ === index && (
                          <div className="px-4 pb-4 text-gray-600 border-t border-gray-100">
                            <p className="pt-4">{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {searchResults.length === 0 && filteredFAQs.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-search-line text-3xl text-gray-400"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">未找到相关内容</h3>
                  <p className="text-gray-600">
                    没有找到与 "<strong>{searchQuery}</strong>" 相关的内容，请尝试其他关键词
                  </p>
                </div>
              )}
            </div>
          ) : (
            // 分类浏览视图
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Sidebar Categories */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">文档分类</h3>
                  <nav className="space-y-2">
                    {categories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => setActiveCategory(category.id)}
                        className={`w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors cursor-pointer whitespace-nowrap ${
                          activeCategory === category.id
                            ? 'bg-teal-50 text-teal-700 border-l-4 border-teal-600'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <i className={`${category.icon} mr-3 text-lg`}></i>
                        {category.name}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>

              {/* Content Area */}
              <div className="lg:col-span-3">
                {/* Articles Grid */}
                <div className="mb-12">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    {categories.find(cat => cat.id === activeCategory)?.name}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {filteredArticles.map((article, index) => (
                      <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer">
                        <div className="flex items-start justify-between mb-4">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">{article.title}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(article.difficulty)}`}>
                            {article.difficulty}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-4">{article.description}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-sm text-gray-500">
                            <i className="ri-time-line mr-1"></i>
                            {article.readTime}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {article.tags.map((tag, tagIndex) => (
                              <span key={tagIndex} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* FAQ Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">常见问题</h2>
                  <div className="space-y-4">
                    {faqs.map((faq, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg">
                        <button
                          onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <span className="font-medium text-gray-900">{faq.question}</span>
                          <i className={`ri-arrow-${expandedFAQ === index ? 'up' : 'down'}-s-line text-gray-500`}></i>
                        </button>
                        {expandedFAQ === index && (
                          <div className="px-4 pb-4 text-gray-600 border-t border-gray-100">
                            <p className="pt-4">{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Contact Support */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">还没找到答案？</h2>
          <p className="text-xl text-gray-600 mb-8">
            我们的技术支持团队随时为您提供帮助
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">邮件支持</h3>
              <p className="text-gray-600 mb-4">发送邮件给我们的技术团队</p>
              <a href="mailto:support@documogul.com" className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer">
                support@documogul.com
              </a>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-xl text-cyan-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">电话支持</h3>
              <p className="text-gray-600 mb-4">工作日 10:00-18:00</p>
              <a href="tel:400-838-9650" className="text-cyan-600 hover:text-cyan-700 font-medium cursor-pointer">
                400-838-9650
              </a>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-chat-3-line text-xl text-emerald-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">在线聊天</h3>
              <p className="text-gray-600 mb-4">即时获得帮助</p>
              <button className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap">
                开始聊天
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
